import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@immo/ui-kit';
import { apiClient } from '@/lib/auth/api-client';
import { XIcon } from '@/components/layout/icons';
import { MailboxAvatar } from '@/components/mailbox-avatar';
import type { MailAddress } from './use-composers';

// A pragmatic, RFC-ish check: one `@`, a non-empty local part, and a dotted
// domain. Deliberately permissive — the backend is the real authority — but
// strict enough to reject obvious typos before send.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

type Suggestion = { readonly id: string; readonly displayName: string; readonly email: string };

export type ComposerRecipientFieldProps = {
  readonly id: string;
  readonly label: string;
  readonly value: ReadonlyArray<MailAddress>;
  readonly onChange: (value: ReadonlyArray<MailAddress>) => void;
  readonly workspaceId: string;
  readonly placeholder?: string;
  readonly invalid?: boolean;
};

function sameEmail(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function ComposerRecipientField({
  id,
  label,
  value,
  onChange,
  workspaceId,
  placeholder,
  invalid = false,
}: ComposerRecipientFieldProps) {
  const [term, setTerm] = useState('');
  const [suggestions, setSuggestions] = useState<ReadonlyArray<Suggestion>>([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const alreadyAdded = useCallback(
    (email: string): boolean => value.some((entry) => sameEmail(entry.email, email)),
    [value],
  );

  const addAddress = useCallback(
    (address: MailAddress) => {
      const email = address.email.trim();

      if (email === '' || alreadyAdded(email)) return;

      onChange([...value, { email, ...(address.name !== undefined ? { name: address.name } : {}) }]);
    },
    [value, onChange, alreadyAdded],
  );

  const removeAt = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange],
  );

  // Debounced contact search. A blank term clears the dropdown without a call.
  useEffect(() => {
    if (debounceRef.current !== null) clearTimeout(debounceRef.current);

    const trimmed = term.trim();

    if (trimmed === '') {
      setSuggestions([]);
      setOpen(false);

      return;
    }

    debounceRef.current = setTimeout(() => {
      void (async () => {
        try {
          const result = await apiClient.contacts.workspaceList({
            pathParams: { workspaceId },
            query: { q: trimmed },
          });

          if (!result.ok) {
            setSuggestions([]);

            return;
          }

          const matches = result.body.data
            .filter((row): row is typeof row & { primaryEmail: string } => row.primaryEmail !== null)
            .map((row) => ({ id: row.id, displayName: row.displayName, email: row.primaryEmail }));

          setSuggestions(matches);
          setOpen(matches.length > 0);
        } catch {
          setSuggestions([]);
        }
      })();
    }, 200);

    return () => {
      if (debounceRef.current !== null) clearTimeout(debounceRef.current);
    };
  }, [term, workspaceId]);

  // Accept the current free-typed term as an address if it is a valid email.
  const commitTerm = useCallback((): boolean => {
    const trimmed = term.trim();

    if (trimmed === '') return true;

    if (isValidEmail(trimmed)) {
      addAddress({ email: trimmed });
      setTerm('');
      setSuggestions([]);
      setOpen(false);

      return true;
    }

    // Keep the invalid text in the box; the parent validates on send.
    return false;
  }, [term, addAddress]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commitTerm();

      return;
    }

    // Backspace on an empty box removes the last chip (Gmail behaviour).
    if (event.key === 'Backspace' && term === '' && value.length > 0) {
      removeAt(value.length - 1);
    }
  };

  return (
    <div className="relative">
      {/* Borderless — the enclosing ComposerFieldRow owns the divider and label; the
          chips + free-text input fill the row height with no box of their own. */}
      <div className="flex flex-wrap items-center gap-1">
        {value.map((entry, index) => {
          const named = entry.name !== undefined && entry.name.trim() !== '';

          return (
            // Selected recipient → a pill leading with the coloured initials avatar,
            // then name + address (or just the address for a bare email).
            <span
              key={`${entry.email}-${index}`}
              className="flex max-w-full items-center gap-1.5 rounded-full bg-neutral-100 py-0.5 pl-0.5 pr-2 text-xs"
            >
              <MailboxAvatar email={entry.email} name={entry.name ?? null} size="sm" />
              <span className="flex min-w-0 items-baseline gap-1.5">
                {named && <span className="truncate font-medium text-text-primary">{entry.name}</span>}
                <span className="truncate text-text-tertiary">{entry.email}</span>
              </span>
              {/* `variant="ghost"` per the slice contract, pinned back from 36px
                  to 16px: the chip is a `py-0.5 text-xs` pill around a
                  `size="sm"` avatar, and the token footprint is wider than the
                  whole recipient row is tall. `size={null}` — not `size="icon"`
                  plus an `h-4 w-4` override — because until IMM-785 `cn` did
                  not classify `h-control-md` as a height, so a `className`
                  override never beat it (BL-103); `size={null}` stays because
                  it states that intent directly. */}
              <Button
                type="button"
                variant="ghost"
                size={null}
                aria-label={`${label}: ${entry.email}`}
                onClick={() => removeAt(index)}
                className="h-4 w-4 shrink-0 text-text-tertiary hover:bg-transparent hover:text-text-primary"
              >
                <XIcon className="h-3 w-3" aria-hidden />
              </Button>
            </span>
          );
        })}
        <input
          id={id}
          type="text"
          aria-label={label}
          aria-invalid={invalid}
          value={term}
          placeholder={value.length === 0 ? placeholder : undefined}
          onChange={(event) => setTerm(event.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => {
            commitTerm();
            // Let a suggestion click register before the dropdown unmounts.
            setTimeout(() => setOpen(false), 150);
          }}
          className="min-w-[8rem] flex-1 bg-transparent py-1 text-base-sm text-text-primary outline-none placeholder:text-text-placeholder"
        />
      </div>

      {open && suggestions.length > 0 && (
        <ul
          role="listbox"
          className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-md border border-border bg-surface py-1 shadow-md"
        >
          {suggestions.map((suggestion) => (
            <li key={suggestion.id}>
              <Button
                type="button"
                variant="ghost"
                size={null}
                // onMouseDown (not onClick) fires before the input's onBlur, so
                // the pick is not lost. It survives the kit migration unchanged —
                // the primitive changed, the event that carries the behaviour
                // did not.
                onMouseDown={(event) => {
                  event.preventDefault();
                  addAddress({ email: suggestion.email, name: suggestion.displayName });
                  setTerm('');
                  setSuggestions([]);
                  setOpen(false);
                }}
                className="h-auto w-full justify-start whitespace-normal rounded-none px-3 py-1.5 text-left font-normal"
              >
                <MailboxAvatar email={suggestion.email} name={suggestion.displayName} size="sm" />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-base-sm text-text-primary">{suggestion.displayName}</span>
                  <span className="truncate text-xs text-text-tertiary">{suggestion.email}</span>
                </span>
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
